import { Button, Center, Group, Input, Pagination, Stack, Table, Title } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { closeAllModals, openModal } from '@mantine/modals';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  APIPropertyFunction,
  APIPropertyItemFunction,
  runApiServiceFunction,
  useAPIsListFunction,
  useAPIsMethods,
} from '@mygpt/app';
import { SvgAdd, SvgSearch } from '@/components/Common/icon';
import { ApiItemEdit, ApiItemEditProps } from '@/components/Datasets/APIs/edit';
import ApiItem from '@/components/Datasets/APIs/item';
import { openRemoveDialog } from '@/components/RemoveDialog';
import TableLoading from '@/components/TableLoading';
import { useDatasetStore } from '@/store/dataset';

import classes from './index.module.css';
import { useParams } from 'react-router-dom';

const APIsList = () => {
  const { t } = useTranslation();
  const matches = useMediaQuery('(min-width: 640px)');

  const { datasetId } = useParams();
  const { data: apis, loadList, loading, page, setPage } = useAPIsListFunction(datasetId, { defaultPageSize: 50 });
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { addAPIs, deleteAPIs, updateAPIs } = useAPIsMethods(datasetId);
  const isLoadedRef = useRef(false);

  const { isCloning, setCloningTipOpened, setCount } = useDatasetStore();

  const selectFAQs = useCallback((selectFAQs: APIPropertyItemFunction) => {
    setSelectedIds((ids) => {
      if (ids.includes(selectFAQs.id)) {
        return ids.filter((id) => id !== selectFAQs.id);
      }
      return [...ids, selectFAQs.id];
    });
  }, []);

  useLayoutEffect(() => {
    if (debouncedSearch) {
      loadList({
        search: debouncedSearch,
      });
    } else if (isLoadedRef.current && datasetId) {
      loadList({ page: 1 });
    }
  }, [datasetId, debouncedSearch, loadList]);

  useEffect(() => {
    isLoadedRef.current = true;
  }, []);

  useEffect(() => {
    setSelectedIds([]);
  }, [debouncedSearch, datasetId, page.page, page.size]);

  useEffect(() => {
    setCount('api', page.total);
  }, [page.total]);

  const handleEdit = (api?: APIPropertyItemFunction) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const isEdit = !!api;
    const onSubmit = async (params: Parameters<NonNullable<ApiItemEditProps['onSubmit']>>[0]) => {
      if (isEdit) {
        await updateAPIs({ datasetId, id: api.id, ...params });
      } else {
        await addAPIs({ datasetId, ...params });
      }
      closeAllModals();
    };

    const onRun = async (question: string, api: APIPropertyFunction) => {
      const res = await runApiServiceFunction({
        datasetId,
        question,
        ...api,
        body: JSON.parse(api.body),
        body_required: JSON.parse(api.body_required),
      });
      return res.data;
    };

    openModal({
      title: api ? t('apis.form.create_title') : t('apis.form.edit_title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 720,
      children: <ApiItemEdit api={api} onSubmit={onSubmit} onRun={onRun} />,
    });
  };

  const handleDelete = (...apiIds: string[]) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const onDelete = async () => {
      await deleteAPIs({ datasetId, apiId: apiIds[0] });
    };
    openRemoveDialog({
      title: t('apis.action.delete.title'),
      description: t('apis.action.delete.description'),
      confirmLabel: t('apis.action.delete.confirm'),
      onDelete,
    });
  };

  const renderContent = () => {
    if (loading) {
      return <TableLoading />;
    }
    if (!apis.length && !debouncedSearch) {
      return (
        <section className="rounded-[20px] bg-white border border-[#F8F6FF]">
          <section className="h-[400px] flex items-center justify-center">
            <Stack align={'center'}>
              <Title order={4} c="gray.5">
                {t('apis.table.empty')}
              </Title>
            </Stack>
          </section>
        </section>
      );
    }
    return (
      <Table highlightOnHover className={classes.table} captionSide="bottom">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Group gap="xs">#</Group>
            </Table.Th>
            <Table.Th>{t('apis.table.name')}</Table.Th>
            <Table.Th>{t('apis.table.description')}</Table.Th>
            <Table.Th>{t('apis.table.type')}</Table.Th>
            <Table.Th>{t('apis.table.url')}</Table.Th>
            <Table.Th>{t('apis.table.action')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {apis.map((item, index) => (
            <ApiItem
              item={item}
              key={index}
              index={index + 1}
              selected={selectedIds.includes(item.id)}
              onSelectedChange={() => selectFAQs(item)}
              onEdit={handleEdit}
              onDelete={(item) => handleDelete(item.id)}
            />
          ))}
        </Table.Tbody>
        {!debouncedSearch && page.pages > 1 && (
          <caption className="bg-white mt-0 pt-3">
            <Center className="mb-4">
              <Pagination
                value={page.page}
                size={matches ? 'md' : 'sm'}
                onChange={(value) => setPage({ page: value })}
                total={page.pages}
                color="violet.5"
              />
            </Center>
          </caption>
        )}
      </Table>
    );
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:justify-between flex-wrap mb-5">
        <div className="flex flex-1 gap-3 items-center lg:justify-start mb-2 lg:mb-0">
          {/* <Button
            color="red"
            leftSection={<SvgTrash />}
            variant="outline"
            className="filled"
            disabled={selectedIds.length === 0}
            onClick={() => handleDelete(...selectedIds)}
          >
            Delete
          </Button> */}
          <Input
            className="grow lg:grow-0 lg:min-w-[30%]"
            leftSection={<SvgSearch />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={t('apis.search_placeholder')}
          />
        </div>
        <Group wrap="nowrap">
          <Button leftSection={<SvgAdd />} onClick={() => handleEdit()}>
            {t('apis.create')}
          </Button>
        </Group>
      </div>
      {renderContent()}
    </div>
  );
};
export default APIsList;
